import { Component, ElementRef, HostListener, Input, Output, EventEmitter, ViewChild, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmPopup } from '../confirm-popup/confirm-popup';
import { trigger, state, style, transition, animate } from '@angular/animations';

export interface AssigneeUser {
  _id: string;
  name: string;
  image?: string;
  assignType?: string;
  email?: string;
  jobrole?: string;
  teamName?: string;
  avatar?: string;
  [key: string]: any;
}

export interface AssigneeSelectionConfig {
  title?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  loadingText?: string;
  noResultsText?: string;
  noChangesMessage?: string;
}

@Component({
  selector: 'app-assignee-selection-expand',
  standalone: true,
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    MatIconModule,
    ConfirmPopup
  ],
  templateUrl: './assignee-selection-expand.html',
  styleUrl: './assignee-selection-expand.scss',
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ 
          height: '0', 
          opacity: 0,
          transform: 'translateY(-8px)',
          overflow: 'hidden'
        }),
        animate('350ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ 
          height: '*', 
          opacity: 1,
          transform: 'translateY(0)',
          overflow: 'hidden'
        }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('250ms cubic-bezier(0.4, 0.0, 1, 1)', style({ 
          height: '0', 
          opacity: 0,
          transform: 'translateY(-8px)',
          overflow: 'hidden'
        }))
      ])
    ])
  ]
})
export class AssigneeSelectionExpand {
  // Input signals using Angular 20 input() function
  @Input() set expanded(value: boolean) {
    this._expanded.set(value);
  }
  @Input() set allAssignableData(value: AssigneeUser[]) {
    this._allAssignableData.set(value);
  }
  @Input() set assignedUsers(value: AssigneeUser[]) {
    this._assignedUsers.set(value);
  }
  @Input() set isLoadingData(value: boolean) {
    this._isLoadingData.set(value);
  }
  @Input() set config(value: AssigneeSelectionConfig) {
    this._config.set(value);
  }

  // Output events
  @Output() assignUsers = new EventEmitter<{
    newlySelectedUsers: AssigneeUser[];
    removedUsers: AssigneeUser[];
  }>();
  @Output() collapse = new EventEmitter<void>();

  // ViewChild
  @ViewChild('assigneeSelect') assigneeSelect!: NgSelectComponent;

  // Private signals
  private _expanded = signal(false);
  private _allAssignableData = signal<AssigneeUser[]>([]);
  private _assignedUsers = signal<AssigneeUser[]>([]);
  private _isLoadingData = signal(false);
  private _config = signal<AssigneeSelectionConfig>({});

  // Public signals
  selectedUsers = signal<AssigneeUser[]>([]);
  selectedUserIds = signal<string[]>([]);
  assigneeSearch = signal('');
  filteredAssignees = signal<AssigneeUser[]>([]);
  showDropdown = signal(false);
  showNoChangesMessage = signal(false);
  showAssignConfirmPopup = signal(false);
  previouslyAssignedUsers = signal<AssigneeUser[]>([]);
  imageLoadError: { [key: string]: boolean } = {};

  // Computed signals
  ngSelectItems = computed(() => {
    const selectedUserIdsSet = new Set(this.selectedUserIds());
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    
    this._allAssignableData().forEach(user => {
      uniqueUsersMap.set(user._id, user);
    });
    
    this._assignedUsers().forEach(user => {
      if (!uniqueUsersMap.has(user._id)) {
        uniqueUsersMap.set(user._id, user);
      }
    });
    
    if (this.showDropdown() && this.assigneeSearch().trim().length > 0) {
      return this.filteredAssignees();
    }
    
    if (this.showDropdown()) {
      return Array.from(uniqueUsersMap.values());
    }
    
    return Array.from(uniqueUsersMap.values());
  });

  // Computed config with defaults
  expandConfig = computed(() => {
    const config = this._config();
    return {
      title: config.title || 'Assign',
      placeholder: config.placeholder || 'Search and select assignees...',
      confirmText: config.confirmText || 'Assign',
      cancelText: config.cancelText || 'Cancel',
      loadingText: config.loadingText || 'Loading...',
      noResultsText: config.noResultsText || 'No assignees found',
      noChangesMessage: config.noChangesMessage || 'No changes made to assignees'
    };
  });

  constructor() {
    // Effect to handle expansion
    effect(() => {
      if (this._expanded()) {
        this.loadSelectedUsers();
        this.resetState();
      }
    });
  }

  // Expand/Collapse control methods
  toggleExpand = () => {
    this._expanded.set(!this._expanded());
    if (this._expanded()) {
      this.showNoChangesMessage.set(false);
      this.imageLoadError = {};
      this.loadSelectedUsers();
      this.selectedUsers.set(this.ensureUniqueUsers(this.selectedUsers()));
      this.assigneeSearch.set('');
      this.filteredAssignees.set([]);
      this.showDropdown.set(true);
      setTimeout(() => {
        this.clearAssigneeSearch();
        if (this.assigneeSelect) {
          this.assigneeSelect.detectChanges();
        }
      }, 100);
    }
  };

  collapsePanel = () => {
    this._expanded.set(false);
    this.selectedUsers.set([]);
    this.selectedUserIds.set([]);
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    this.clearAssigneeSearch();
    this.collapse.emit();
  };

  // User selection methods
  onUserSelectionChange = (userIds: string[]) => {
    const previouslySelectedIdsSet = new Set(this.previouslyAssignedUsers().map(u => u._id));
    const validUserIds = userIds.filter(id => true);
    const currentSelectedSet = new Set(this.selectedUserIds());
    const newIds = validUserIds.filter(id => !currentSelectedSet.has(id));
    const finalUserIds = [...new Set(validUserIds)];
    
    this.selectedUserIds.set(finalUserIds);
    
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    
    this._assignedUsers().forEach(user => {
      const displayName = user.name || user.jobrole || user.teamName || 'Unknown';
      uniqueUsersMap.set(user._id, {
        ...user,
        _id: user._id,
        name: displayName,
        image: user.image || user.avatar || user.image
      });
    });
    
    this._allAssignableData().forEach(user => {
      if (!uniqueUsersMap.has(user._id)) {
        const displayName = user.name || user.jobrole || user.teamName || 'Unknown';
        uniqueUsersMap.set(user._id, {
          ...user,
          _id: user._id,
          name: displayName,
          image: user.image || user.avatar || user.image
        });
      }
    });
    
    this.selectedUsers.set(
      finalUserIds
        .map(id => uniqueUsersMap.get(id))
        .filter(user => user !== undefined)
        .map(user => {
          const displayName = user!.name || user!.jobrole || user!.teamName || 'Unknown';
          return {
            _id: user!._id,
            name: displayName,
            image: user!.image || user!.avatar,
            assignType: user!.assignType 
          };
        })
    );
    
    this.checkForChanges();
  };

  removeSelectedUser = (index: number) => {
    const arr = [...this.selectedUsers()];
    const removedUser = arr.splice(index, 1)[0];
    this.selectedUsers.set(this.ensureUniqueUsers(arr));
    this.selectedUserIds.set(this.selectedUserIds().filter(id => id !== removedUser._id));
    this.checkForChanges();
  };

  removeSelectedUserFromNgSelect = (item: AssigneeUser, event: Event) => {
    event.stopPropagation(); 
    const arr = [...this.selectedUsers()];
    const index = arr.findIndex(user => user._id === item._id);
    if (index !== -1) {
      arr.splice(index, 1);
      this.selectedUsers.set(this.ensureUniqueUsers(arr));
      this.selectedUserIds.set(this.selectedUserIds().filter(id => id !== item._id));
    }
    this.checkForChanges();
  };

  // Search and filter methods
  filterAssignees = () => {
    const search = this.assigneeSearch().toLowerCase();
    const selectedUserIdsSet = new Set(this.selectedUserIds());
    
    const allUsersMap = new Map<string, AssigneeUser>();
    this._allAssignableData().forEach(user => {
      allUsersMap.set(user._id, user);
    });
    const currentData = Array.from(allUsersMap.values());
    
    this.filteredAssignees.set(
      currentData.filter(item => {
        if (selectedUserIdsSet.has(item._id)) {
          return false;
        }
        const displayName = this.getDisplayName(item);
        return displayName.toLowerCase().includes(search);
      })
    );
  };

  onAssigneeSearch = (event: any) => {
    this.assigneeSearch.set(event.term);
    if (this.assigneeSearch() && this.assigneeSearch().trim().length > 0) {
      this.showDropdown.set(true);
      this.filterAssignees();
    } else {
      this.showDropdown.set(false);
      this.filteredAssignees.set([]);
    }
  };

  // NgSelect event handlers
  onAssigneeAdd = () => this.forceClearSearchWithDelay();
  onAssigneeRemove = () => {};
  onAssigneeSelectionChanges = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    setTimeout(() => {
      this.clearAssigneeSearch();
    }, 5);
  };
  onAssigneeSelect = () => this.forceClearSearchWithDelay();
  onAssigneeChange = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set(this._allAssignableData());
    this.showDropdown.set(false);
    this.clearAssigneeSearch();
  };
  onAssigneeBlur = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set(this._allAssignableData());
    this.clearAssigneeSearch();
  };
  onAssigneeOpen = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(true);
  };
  onAssigneeClose = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    this.clearAssigneeSearch();
  };

  // Search clearing methods
  clearAssigneeSearch = () => {
    if (this.assigneeSelect) {
      this.assigneeSelect.searchTerm = '';
      if (this.assigneeSelect.searchInput && this.assigneeSelect.searchInput.nativeElement) {
        const searchInput = this.assigneeSelect.searchInput.nativeElement;
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.assigneeSelect.detectChanges();
      if (this.assigneeSelect.searchInput) {
        this.assigneeSelect.searchInput.nativeElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
      }
    }
    this.filteredAssignees.set([]);
  };

  forceClearSearch = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    if (this.assigneeSelect && this.assigneeSelect.searchInput) {
      const searchInput = this.assigneeSelect.searchInput.nativeElement;
      searchInput.value = '';
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      searchInput.dispatchEvent(new Event('change', { bubbles: true }));
      searchInput.dispatchEvent(new Event('blur', { bubbles: true }));
    }
  };

  forceClearSearchWithDelay = () => {
    setTimeout(() => {
      this.forceClearSearch();
    }, 1);
  };

  // Assignment confirmation methods
  onAssignUsersClick = () => this.showAssignConfirmPopup.set(true);
  
  confirmAssignUsers = () => {
    this.showAssignConfirmPopup.set(false);
    this._assignUsersWithApi();
  };
  
  cancelAssignUsers = () => this.showAssignConfirmPopup.set(false);

  // Helper methods
  private ensureUniqueUsers(users: AssigneeUser[]): AssigneeUser[] {
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    users.forEach(user => {
      uniqueUsersMap.set(user._id, user);
    });
    return Array.from(uniqueUsersMap.values());
  }

  private loadSelectedUsers = () => {
    this.previouslyAssignedUsers.set([...this._assignedUsers()]);
    this.selectedUsers.set([]);
    this.selectedUserIds.set([]);
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    this._assignedUsers().forEach(user => {
      const displayName = user.name || user.jobrole || user.teamName || (user as any)['jobroles']?.jobrole || 'Unknown';
      uniqueUsersMap.set(user._id, {
        ...user,
        _id: user._id,
        name: displayName,
        image: user.image || user.avatar || user.image
      });
    });
    const arr = Array.from(uniqueUsersMap.values());
    this.selectedUsers.set(arr);
    this.selectedUserIds.set(arr.map(user => user._id));
  };

  private resetState = () => {
    this.showNoChangesMessage.set(false);
    this.showAssignConfirmPopup.set(false);
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    this.imageLoadError = {};
  };

  private checkForChanges = () => {
    const newlySelectedUsers = this.selectedUsers().filter(selectedUser => 
      !this.previouslyAssignedUsers().some(prevUser => prevUser._id === selectedUser._id));
    const removedUsers = this.previouslyAssignedUsers().filter(prevUser => 
      !this.selectedUsers().some(selectedUser => selectedUser._id === prevUser._id));
    
    const hasChanges = newlySelectedUsers.length > 0 || removedUsers.length > 0;
    this.showNoChangesMessage.set(!hasChanges);
  };

  private _assignUsersWithApi = () => {
    const newlySelectedUsers = this.selectedUsers().filter(selectedUser => 
      !this.previouslyAssignedUsers().some(prevUser => prevUser._id === selectedUser._id))
        .map(selectedUser => ({...selectedUser, assignType: selectedUser.assignType}));
    const removedUsers = this.previouslyAssignedUsers().filter(prevUser => 
      !this.selectedUsers().some(selectedUser => selectedUser._id === prevUser._id))
      .map(prevUser => ({...prevUser, assignType: prevUser.assignType}));
    
    if (newlySelectedUsers.length === 0 && removedUsers.length === 0) {
      this.showNoChangesMessage.set(true);
      return;
    }

    this.assignUsers.emit({
      newlySelectedUsers,
      removedUsers
    });
    this.collapsePanel();
  };

  getDisplayName = (item: any): string => { 
    if (!item) return 'Unknown';
    return item.name || item.jobrole || item.teamName || item.jobroles?.jobrole || 'Unknown';
  };

  getAssigneeType(item: any): 'PEOPLE' | 'TEAM' | 'JOBROLE' {
    if (!item) return 'PEOPLE';
    
    if (item.assignType) {
      if (item.assignType === 'TEAM' || item.assignType === 'team') return 'TEAM';
      if (item.assignType === 'JOBROLE' || item.assignType === 'jobrole' || item.assignType === 'JOB_ROLE') return 'JOBROLE';
      return 'PEOPLE';
    }
    
    if (item.teamName && !item.name && !item.jobrole) {
      return 'TEAM';
    }
    
    if (item.jobrole && !item.name && !item.teamName) {
      return 'JOBROLE';
    }
    
    if (item.email || item.image || item.avatar) {
      return 'PEOPLE';
    }
    
    return 'PEOPLE';
  }

  getTypeLabel(item: any): string {
    const type = this.getAssigneeType(item);
    switch (type) {
      case 'TEAM':
        return 'Team';
      case 'JOBROLE':
        return 'Job Role';
      case 'PEOPLE':
      default:
        return 'Member';
    }
  }

  getTypeBadgeClass(item: any): string {
    const type = this.getAssigneeType(item);
    switch (type) {
      case 'TEAM':
        return 'type-badge type-badge-team';
      case 'JOBROLE':
        return 'type-badge type-badge-jobrole';
      case 'PEOPLE':
      default:
        return 'type-badge type-badge-person';
    }
  }

  getInitials(name: string): string {
    if (!name || name.trim() === '') {
      return '?';
    }

    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  getAvatarColor(userId: string): string {
    if (!userId) return '#6b7280';
    
    const colors = [
      '#8b5cf6', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#ec4899',
      '#14b8a6', '#f97316', '#6366f1', '#a855f7', '#06b6d4', '#84cc16',
    ];

    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  }

  hasValidImage(user: AssigneeUser): boolean {
    const imageUrl = user.image || user.avatar;
    return !!(imageUrl && !this.imageLoadError[user._id]);
  }

  onImageError(userId: string): void {
    this.imageLoadError[userId] = true;
  }

  // Getters for template access
  get expanded() {
    return this._expanded();
  }

  get allAssignableData() {
    return this._allAssignableData();
  }

  get assignedUsers() {
    return this._assignedUsers();
  }

  get isLoadingData() {
    return this._isLoadingData();
  }
}

